/**
 * A request that never got an answer.
 *
 * `fetch` rejects when the connection drops, the device is offline, or
 * the request is aborted, and each browser words it its own way — Safari
 * "Load failed", Chrome "Failed to fetch", Firefox "NetworkError when
 * attempting to fetch resource." That wording reached the screen as it
 * was. Every request is made through `reachable`, so a rejection arrives
 * as this one error with one sentence, in the player's language.
 *
 * Whether the server acted on the request is unknown: it may have
 * arrived and only the answer been lost. A caller that sent something
 * which must not happen twice keeps that in mind (the action path
 * resends under the same idempotency key).
 */
export class NetworkUnreachableError extends Error {
  override readonly name = "NetworkUnreachableError";

  constructor(cause?: unknown) {
    super(networkUnreachableMessage(), { cause });
  }
}

function networkUnreachableMessage(): string {
  const fallback = "서버에 연결할 수 없어요. 인터넷 연결을 확인해 주세요.";
  const i18n = (
    globalThis as unknown as {
      __arkheI18n?: { t: (k: string) => string };
    }
  ).__arkheI18n;
  return i18n === undefined ? fallback : i18n.t("error.network_unreachable");
}

/** `fetch`, with a rejection turned into `NetworkUnreachableError`. */
export function reachable(
  fetchImpl: typeof fetch,
): (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> {
  return async (input, init) => {
    try {
      return await fetchImpl(input, init);
    } catch (err) {
      throw new NetworkUnreachableError(err);
    }
  };
}
